/**
 * Onglet Liens candidats — génération d'un lien à usage unique (le token
 * complet n'est renvoyé qu'UNE fois, à la création, cf. contrat : la liste
 * des candidats ne le réexpose jamais) + table des liens déjà émis.
 *
 * 01-11-2026 — au nouveau thème : le formulaire est une `Section` (`Champ` +
 * `Button`), les liens fraîchement générés des `Ligne` avec un bouton « copier »
 * en icône (`Infobulle`), et la table des liens émis le `Tableau` de la
 * librairie (sous 640 px : Candidat · État seulement).
 */
import { useMemo, useState } from "react";
import { useMutation } from "@tanstack/react-query";

import { Badge, Button, Champ, Ligne, Lignes, Saisie, Section, Tableau, Temps, Vide, type Colonne } from "@/shared/ui";
import { Download, Plus } from "@/shared/ui/icons";
import { FUSEAU_DES_SHIFTS } from "@/shared/shiftTime";

import { adminApi, CANDIDATES_CSV_HREF } from "../api";
import { formatPercent, statusLabel } from "../format";
import type { CandidateListItem, CreateCandidateResponse } from "../types";

interface LinksTabProps {
  candidates: CandidateListItem[];
  onCreated: () => void;
}

export function LinksTab({ candidates, onCreated }: LinksTabProps) {
  const [label, setLabel] = useState("");
  const [createdLinks, setCreatedLinks] = useState<CreateCandidateResponse[]>([]);

  const createMutation = useMutation({
    mutationFn: () => adminApi.createCandidate(label.trim() || undefined),
    onSuccess: (res) => {
      setCreatedLinks((prev) => [res, ...prev]);
      setLabel("");
      onCreated();
    },
  });

  const sortedCandidates = useMemo(
    () =>
      [...candidates].sort(
        (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [candidates],
  );

  const colonnes = useMemo<Colonne<CandidateListItem>[]>(
    () => [
      {
        cle: "candidat",
        titre: "Candidat",
        essentiel: true,
        rendu: (c) =>
          c.first_name ? (
            <span className="font-medium text-fg">{`${c.first_name} ${c.last_name ?? ""}`.trim()}</span>
          ) : (
            <span className="text-faint">— pas encore ouvert —</span>
          ),
      },
      {
        cle: "source",
        titre: "Source",
        secondaire: true,
        rendu: (c) => <span className="text-muted">{c.source_detail || c.source || "—"}</span>,
      },
      {
        cle: "cree",
        titre: "Créé le",
        secondaire: true,
        largeur: 110,
        rendu: (c) => (
          <Temps.Heure instant={Date.parse(c.created_at)} fuseau={FUSEAU_DES_SHIFTS} format="jour" className="text-muted" />
        ),
      },
      {
        cle: "etat",
        titre: "État",
        essentiel: true,
        largeur: 130,
        rendu: (c) => (
          <Badge ton={c.status === "submitted" ? "vert" : c.status === "started" ? "ambre" : "gris"}>
            {statusLabel(c.status)}
          </Badge>
        ),
      },
      {
        cle: "resultat",
        titre: "Résultat",
        secondaire: true,
        largeur: 120,
        rendu: (c) => (
          <span className="text-muted">
            {c.status === "submitted" && c.score?.fault_catch_rate != null
              ? formatPercent(c.score.fault_catch_rate)
              : c.status === "started"
                ? "en cours…"
                : "—"}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="flex flex-col gap-4">
      <Section titre="Générer un lien candidat">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <Champ
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="Étiquette (optionnel) — ex. Camille D."
              className="sm:max-w-[360px] sm:flex-1"
            />
            <Button
              variant="accent"
              size="lg"
              icon={<Plus className="h-4 w-4" />}
              disabled={createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              Nouveau lien
            </Button>
          </div>

          {createdLinks.length === 0 ? (
            <Vide taille="sm" titre="Aucun lien généré pendant cette session pour l'instant." />
          ) : (
            <Lignes>
              {createdLinks.map((link) => (
                <Ligne
                  key={link.id}
                  etiquette={link.url}
                  apres={<Saisie.BoutonCopier valeur={link.url} iconeSeule variant="ghost" size="lg" texte="Copier" texteCopie="Copié !" />}
                />
              ))}
            </Lignes>
          )}

          <p className="text-[12px] leading-relaxed text-faint">
            Chaque lien est à usage unique — pas de seconde tentative sans
            nouveau lien. Le lien complet n'est affiché qu'une seule fois, ici,
            à la création (les réponses attendues, elles, ne quittent jamais le
            serveur).
          </p>
        </div>
      </Section>

      <Tableau<CandidateListItem>
        colonnes={colonnes}
        lignes={sortedCandidates}
        cle={(c) => c.id}
        titre="Liens émis"
        outils={
          <Button
            variant="secondary"
            size="md"
            icon={<Download className="h-3.5 w-3.5" />}
            href={CANDIDATES_CSV_HREF}
            target="_blank"
          >
            Exporter CSV
          </Button>
        }
        vide="Aucun lien émis pour l'instant."
        hauteur={560}
        hauteurLigne={52}
      />
    </div>
  );
}
